import type { CoachJourneyInput } from '../schema';

/** The creator's posts are read, or could not be: either way the journey goes on. */
const READING_DONE = ['node.ready', 'node.failed'] as const;

/**
 * Board onboarding as a journey: Poppy lays out a starter board (brand notes, the IG/TikTok
 * hooks framework, a script chat wired to both), the person adds a creator they love, the
 * chat writes hooks in that style, and Copilot turns one into a reel script.
 *
 * Every piece is data: the layout is the `short-form-starter` template (templates.ts), the
 * board changes are actions (actions.ts) done through the board's own operations. Not
 * started anywhere yet; the app starts it with `coach.start('board-starter')`.
 */
export const boardStarterJourney: CoachJourneyInput = {
  id: 'board-starter',
  version: 1,
  name: 'Set up your board',
  description: 'A starter board, a creator you love, your first hooks, and a script.',
  steps: [
    {
      id: 'open',
      display: 'hidden',
      title: 'Open a board.',
      body: 'Open a board you can edit to set it up.',
      on: [{ event: 'board.entered', match: ['boardId'], bind: ['boardId'], to: 'seed' }],
    },
    {
      id: 'seed',
      display: 'toast',
      title: 'Setting up your board.',
      body: 'Setting up your board, hold tight 🪄✨',
      do: {
        action: 'board.seed',
        params: { template: 'short-form-starter' },
        bind: { chatNodeId: 'chatNodeId', groupNodeId: 'brandNodeId' },
        then: 'tour',
      },
    },
    {
      id: 'tour',
      chapter: 1,
      title: 'This is your board 🎉',
      body: 'Your brand notes and a proven hooks framework are wired into your Script writer chat. Fill in the notes and every script sounds like YOU 🔥',
      focus: { node: 'brandNodeId' },
      action: { label: 'Next', to: 'creator' },
    },
    {
      id: 'creator',
      chapter: 2,
      title: 'Add a creator you love 😍',
      body: 'I’ll study their posts so your chat writes hooks in a style that already goes viral 🚀',
      focus: { node: 'chatNodeId' },
      form: { id: 'creator-profile', bind: 'creatorUrl', submit: 'Add creator' },
      do: {
        action: 'board.add-link',
        params: { urlFrom: 'creatorUrl', groupTitle: 'Inspiration', connectTo: 'chatNodeId' },
        bind: { nodeId: 'creatorNodeId' },
        then: 'reading',
      },
      action: { label: 'Skip', to: 'ask' },
    },
    {
      id: 'reading',
      chapter: 2,
      display: 'toast',
      title: 'Studying their posts.',
      body: 'Studying their posts 👀 this takes about a minute...',
      // A reload lands on the next step: the creator is on the board either way.
      resumeAt: 'ask',
      on: READING_DONE.map(event => ({
        event,
        match: ['boardId'],
        matchAs: { nodeId: 'creatorNodeId' },
        to: 'ask',
      })),
    },
    {
      id: 'ask',
      chapter: 3,
      title: 'Ask for your first hooks 🎣',
      body: 'Ask your Script writer for 5 hooks for your next video, or let me ask for you ✨',
      show: [{ anchor: 'chat.input' }],
      focus: { node: 'chatNodeId' },
      action: { label: 'Ask for me', to: 'asking' },
      on: [{ event: 'chat.sent', match: ['boardId', 'chatNodeId'], to: 'writing' }],
    },
    {
      id: 'asking',
      chapter: 3,
      display: 'toast',
      title: 'Asking your chat.',
      body: 'Asking your Script writer ✨',
      resumeAt: 'ask',
      do: {
        action: 'chat.ask',
        params: {
          chat: 'chatNodeId',
          prompt:
            'Write 5 scroll-stopping hooks for my next short-form video, using my brand notes and the hooks framework.',
        },
        then: 'copilot',
        onError: 'ask',
      },
    },
    {
      id: 'writing',
      chapter: 3,
      display: 'toast',
      title: 'Your chat is writing.',
      body: 'Your Script writer is cooking up your hooks 🍳🔥',
      resumeAt: 'copilot',
      on: [{ event: 'chat.replied', match: ['boardId', 'chatNodeId'], to: 'copilot' }],
    },
    {
      id: 'copilot',
      chapter: 4,
      title: 'Turn a hook into a reel 🎬',
      body: 'Copilot can turn your favorite hook into a reel script you can film. Your message is ready, just hit send 🚀',
      show: [
        { anchor: 'copilot.input' },
        {
          anchor: 'toolbar.copilot',
          title: 'Open Copilot ✨',
          body: 'Click Copilot to turn your favorite hook into a reel script 🎬',
        },
      ],
      do: {
        action: 'copilot.fill',
        params: {
          prompt:
            'Take the best hook from my Script writer chat and turn it into a 30-second reel script.',
        },
      },
      on: [{ event: 'copilot.sent', match: ['boardId'], to: 'end' }],
    },
  ],
};
